import type { ReactElement } from "react";
import {
  Box,
  Skeleton,
  Stat,
  StatLabel,
  StatNumber,
  forwardRef,
  useBreakpointValue,
} from "@chakra-ui/react";
import type { BoxProps, StatProps } from "@chakra-ui/react";

interface BasicInfoContainerProps extends BoxProps {
  columnCount?: number;
}

const BasicInfoContainer = forwardRef<BasicInfoContainerProps, "div">(
  ({ children, sx = {}, columnCount, ...props }, ref): ReactElement => {
    const defaultColumnCount = useBreakpointValue([1, 1, 1, 2]);

    return (
      <Box
        ref={ref}
        sx={{
          columnCount: columnCount || defaultColumnCount,
          columnGap: "1rem",
          ...sx,
        }}
        mb={-4}
        {...props}
      >
        {children}
      </Box>
    );
  }
);

type BasicInfoItemProps = {
  label?: string;
  isLoaded?: boolean;
  showEmpty?: boolean;
} & StatProps;

const BasicInfoItem = ({
  label = "",
  children = "",
  sx = {},
  isLoaded = false,
  showEmpty = false,
  ...props
}: BasicInfoItemProps): ReactElement => {
  let internalChildren = children;
  if (
    isLoaded &&
    (!children ||
      (Array.isArray(children) &&
        (!children.length || children.every((item) => !item))))
  ) {
    internalChildren = "--";
  }

  // Remove this block to show "--" instead of removing the element
  if (!showEmpty && internalChildren === "--") {
    return null;
  }

  return (
    <Stat
      pb={4}
      sx={{ breakInside: "avoid", pageBreakInside: "avoid", ...sx }}
      {...props}
    >
      {isLoaded ? (
        <>
          <StatLabel
            fontSize="xs"
            textTransform="uppercase"
            letterSpacing="wider"
          >
            {label}
          </StatLabel>
          <StatNumber fontSize="md">{internalChildren}</StatNumber>
        </>
      ) : (
        <>
          <Skeleton h="16px" w={isLoaded ? "auto" : "80px"} mb={1} />
          <Skeleton h="22px" w="100%" />
        </>
      )}
    </Stat>
  );
};

export { BasicInfoContainer, BasicInfoItem };
